import React, { useState, useEffect, useRef } from 'react';
import AceEditor from 'react-ace';
import Typography from '@mui/material/Typography';
import 'ace-builds/src-noconflict/mode-groovy';
import 'ace-builds/src-noconflict/theme-cloud9_day';
import { Box, Button, Stack } from '@mui/material';
import WebpageDialog from './WebpageDialog';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import GroovyDialog from './GroovyDialog';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ace from 'ace-builds/src-noconflict/ace';
import { useExternalLink } from '../../externallink/ExternalLink';

const injectCustomGroovyHighlighting = (variables = []) => {
    const GroovyHighlightRules = ace.require('ace/mode/groovy_highlight_rules').GroovyHighlightRules;

    // Define custom highlighting rules by extending GroovyHighlightRules
    function CustomGroovyHighlightRules() {
        // Call the parent constructor
        GroovyHighlightRules.call(this);

        // Dynamically build regex for the variables list
        if (variables.length > 0) {
            const variableRegex = `\\b(${variables.join('|')})\\b`; // Match any word in the variables list

            // Add custom rule for variables to be highlighted as variables
            this.$rules["start"].unshift({
                token: "variable", // token type for variable
                regex: variableRegex // dynamic regex matching the variable names
            });
        }
    }

    // Inherit GroovyHighlightRules
    CustomGroovyHighlightRules.prototype = Object.create(GroovyHighlightRules.prototype);
    CustomGroovyHighlightRules.prototype.constructor = CustomGroovyHighlightRules;

    const GroovyMode = ace.require('ace/mode/groovy').Mode;

    // Define a custom Groovy mode by extending GroovyMode
    function CustomGroovyMode() {
        GroovyMode.call(this);
        this.HighlightRules = CustomGroovyHighlightRules;
    }

    // Inherit GroovyMode
    CustomGroovyMode.prototype = Object.create(GroovyMode.prototype);
    CustomGroovyMode.prototype.constructor = CustomGroovyMode;

    return new CustomGroovyMode();
};

const GroovyPanel = ({ id, name, defaultValue, onChange, label, height, disabled = false, variables = [], hideOpenInNewButton = false }) => {

    const [value, setValue] = useState(defaultValue);
    const [showDialog, setShowDialog] = useState(false);
    const [showCodeDialog, setShowCodeDialog] = useState(false);
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.editor.setReadOnly(disabled);
        }
    }, [disabled]);

    const groovyDocUrl = useExternalLink("groovy-doc");

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    const handleChange = (value) => {
        const event = {
            target: {
                id: id,
                name: name,
                value: value,
            }
        };
        setValue(value);
        if (onChange) {
            onChange(event);
        }
    };

    const handleDialogApply = (value) => {
        setValue(value);
        setShowCodeDialog(false);
        const event = {
            target: {
                id: id,
                name: name,
                value: value,
            }
        };
        if (onChange) {
            onChange(event);
        }
    };

    useEffect(() => {
        const customGroovyMode = injectCustomGroovyHighlighting(variables);
        if (editorRef.current) {
            editorRef.current.editor.getSession().setMode(customGroovyMode);
        }
    }, [variables]);

    return (
        <>
            {showDialog && <WebpageDialog title='GroovyDoc' url={groovyDocUrl} onClose={() => setShowDialog(false)} />}

            {showCodeDialog && (
                <GroovyDialog
                    title={label || "Script"}
                    script={value}
                    onApply={handleDialogApply}
                    onClose={() => setShowCodeDialog(false)}
                />
            )}
            <Box sx={{ height: `${height || "100%"}`, display: "flex", flexDirection: "column", }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {label ? (
                        <Typography variant="p">{label}</Typography>
                    ) : (
                        <span />
                    )}
                    <Stack direction='row'>
                        {!hideOpenInNewButton && <Button startIcon={<OpenInNewIcon />} onClick={() => setShowCodeDialog(true)}>Open In New Window</Button>}
                        <Button startIcon={<WebAssetIcon />} onClick={() => setShowDialog(true)}>View GroovyDoc</Button>
                    </Stack>
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                    <AceEditor
                        mode="groovy"
                        theme="cloud9_day"
                        onChange={handleChange}
                        name={name}
                        editorProps={{ $blockScrolling: true }}
                        value={value}
                        fontSize={14}
                        width="100%"
                        height='100%'
                        setOptions={{
                            highlightActiveLine: true,
                            highlightSelectedWord: true,
                            enableBasicAutocompletion: true,
                        }}
                        disabled={disabled}
                        ref={editorRef}
                    />
                </Box>
            </Box>
        </>
    );
};

export default GroovyPanel;
